import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { executionsAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import {
  ArrowLeft,
  XCircle,
  Clock,
  Calendar,
  Webhook,
  FileText,
  Download,
  Timer,
  Activity,
  Play,
  StopCircle,
  AlertCircle,
  RotateCw,
} from 'lucide-react';
import {
  formatDate,
  formatDuration,
  getStatusColor,
  getLogLevelColor,
  formatTimeSaved,
} from '@/shared/lib/utils';
import type { Execution, Log, AxiosErrorResponse } from '@/shared/types';
import { AutomationType } from '@/shared/types';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { toast } from '@/shared/lib/toast';
import { Breadcrumbs } from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';

export default function ExecutionDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmRetryOpen, setConfirmRetryOpen] = useState(false);

  // Fetch execution
  const { data: execution, isLoading } = useQuery({
    queryKey: ['execution', id],
    queryFn: async () => {
      const response = await executionsAPI.getById(id!);
      return response.data.data as Execution;
    },
    enabled: !!id,
  });

  // Fetch logs
  const { data: logs } = useQuery({
    queryKey: ['execution-logs', id],
    queryFn: async () => {
      const response = await executionsAPI.getLogs(id!);
      return response.data.data as Log[];
    },
    enabled: !!id,
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: () => executionsAPI.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['execution', id] });
      toast.success('Execução cancelada', 'A execução foi cancelada com sucesso.');
    },
    onError: (error: AxiosErrorResponse) => {
      toast.error('Erro ao cancelar', error.response?.data?.error?.message);
    },
  });

  // Retry mutation
  const retryMutation = useMutation({
    mutationFn: () => executionsAPI.retry(id!),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['executions'] });
      const newExecutionId = response.data?.data?.id;
      toast.success(
        'Automação reenfileirada!',
        newExecutionId
          ? `Nova execução criada com sucesso. ID: ${newExecutionId.substring(0, 8)}...`
          : 'A automação foi reenfileirada e será processada em breve.'
      );
    },
    onError: (error: AxiosErrorResponse) => {
      toast.error('Erro ao reexecutar', error.response?.data?.error?.message || 'Erro desconhecido');
    },
  });

  const handleCancel = () => {
    setConfirmCancelOpen(true);
  };

  const handleConfirmCancel = () => {
    cancelMutation.mutate();
    setConfirmCancelOpen(false);
  };

  const handleRetry = () => {
    setConfirmRetryOpen(true);
  };

  const handleConfirmRetry = () => {
    retryMutation.mutate();
    setConfirmRetryOpen(false);
  };

  const handleExportLogs = () => {
    if (!logs) return;

    const content = logs
      .map((log) => `[${log.timestamp}] [${log.level}] ${log.message}`)
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `execution-${id}-logs.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Breadcrumbs Skeleton */}
        <div className="h-5 w-72 bg-muted animate-pulse rounded" />

        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-10 w-10 bg-muted animate-pulse rounded" />
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="h-8 w-48 bg-muted animate-pulse rounded" />
                <div className="h-6 w-24 bg-muted animate-pulse rounded-full" />
              </div>
              <div className="h-5 w-64 bg-muted animate-pulse rounded" />
            </div>
          </div>
          <div className="flex space-x-2">
            <div className="h-10 w-40 bg-muted animate-pulse rounded" />
          </div>
        </div>

        {/* Info Cards Skeleton */}
        <div className="grid gap-4 md:grid-cols-5">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-card border rounded-lg p-6 animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-muted rounded-full" />
                <div className="space-y-2">
                  <div className="h-4 w-16 bg-muted rounded" />
                  <div className="h-5 w-24 bg-muted rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Details Cards Skeleton */}
        <div className="grid gap-4 md:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-card border rounded-lg p-6 animate-pulse">
              <div className="h-6 w-32 bg-muted rounded mb-4" />
              <div className="space-y-4">
                {[...Array(5)].map((_, j) => (
                  <div key={j} className="space-y-2">
                    <div className="h-4 w-32 bg-muted rounded" />
                    <div className="h-4 w-full bg-muted rounded" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Logs Skeleton */}
        <div className="bg-card border rounded-lg p-6 animate-pulse">
          <div className="h-6 w-40 bg-muted rounded mb-4" />
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="border rounded-lg p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="h-5 w-16 bg-muted rounded-full" />
                      <div className="h-4 w-24 bg-muted rounded" />
                    </div>
                    <div className="h-4 w-full bg-muted rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/executions">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Execução não encontrada</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: 'Execuções', href: '/executions' },
          { label: `Execução ${execution.id.substring(0, 8)}...` },
        ]}
      />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/executions">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-3xl font-bold text-foreground">Execução</h1>
              <Badge className={getStatusColor(execution.status)}>{execution.status}</Badge>
            </div>
            <p className="text-muted-foreground mt-1 font-mono text-sm">ID: {execution.id}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          {/* Retry button - show for failed/error/cancelled executions */}
          {(execution.status === 'ERROR' ||
            execution.status === 'TIMEOUT' ||
            execution.status === 'CANCELLED') && (
            <Button
              variant="default"
              onClick={handleRetry}
              disabled={retryMutation.isPending}
            >
              <RotateCw className="h-4 w-4 mr-2" />
              {retryMutation.isPending ? 'Reenfileirando...' : 'Reexecutar'}
            </Button>
          )}
          {execution.status === 'RUNNING' && (
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Cancelar Execução
            </Button>
          )}
          <Button variant="outline" onClick={handleExportLogs}>
            <Download className="h-4 w-4 mr-2" />
            Exportar Logs
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              {execution.automation?.type === AutomationType.CRON ? (
                <Calendar className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              ) : (
                <Webhook className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              )}
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  {execution.automation?.type === AutomationType.CRON ? 'Scheduler' : 'Event'}
                </p>
                <Link
                  to={`/automations/${execution.automationId}`}
                  className={`font-medium hover:underline block ${
                    execution.automation?.type === AutomationType.CRON
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-purple-600 dark:text-purple-400'
                  }`}
                >
                  {execution.automation?.name || 'N/A'}
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <Calendar className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Início</p>
                <p className="font-medium text-foreground">
                  {formatDate(execution.startedAt, 'dd/MM/yyyy HH:mm:ss')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <Clock className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Duração</p>
                <p className="font-medium text-foreground">
                  {execution.duration
                    ? formatDuration(execution.duration)
                    : execution.status === 'RUNNING'
                      ? 'Em execução...'
                      : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <Timer className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Economia</p>
                <p className="font-medium text-foreground">
                  {execution.metadata &&
                  typeof execution.metadata === 'object' &&
                  'timeSavedMinutes' in execution.metadata
                    ? formatTimeSaved(execution.metadata.timeSavedMinutes as number)
                    : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <FileText className="h-8 w-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Logs</p>
                <p className="font-medium text-foreground">{logs?.length || 0} entradas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Execution Info */}
        <Card>
          <CardHeader>
            <CardTitle>Informações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-foreground flex items-center">
                <Activity className="h-4 w-4 mr-1" />
                Status
              </p>
              <Badge className={`mt-1 ${getStatusColor(execution.status)}`}>
                {execution.status}
              </Badge>
            </div>

            <div>
              <p className="text-sm font-medium text-foreground flex items-center">
                <Play className="h-4 w-4 mr-1" />
                Início
              </p>
              <p className="text-sm text-muted-foreground">
                {formatDate(execution.startedAt, 'dd/MM/yyyy HH:mm:ss')}
              </p>
            </div>

            {execution.finishedAt && (
              <div>
                <p className="text-sm font-medium text-foreground flex items-center">
                  <StopCircle className="h-4 w-4 mr-1" />
                  Fim
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatDate(execution.finishedAt, 'dd/MM/yyyy HH:mm:ss')}
                </p>
              </div>
            )}

            {execution.duration && (
              <div>
                <p className="text-sm font-medium text-foreground flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Duração
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatDuration(execution.duration)}
                </p>
              </div>
            )}

            {execution.metadata &&
              typeof execution.metadata === 'object' &&
              'timeSavedMinutes' in execution.metadata && (
                <div>
                  <p className="text-sm font-medium text-foreground flex items-center">
                    <Timer className="h-4 w-4 mr-1" />
                    Tempo Economizado
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatTimeSaved(execution.metadata.timeSavedMinutes as number)}
                  </p>
                </div>
              )}

            {execution.error && (
              <div>
                <p className="text-sm font-medium text-destructive flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Erro
                </p>
                <div className="mt-1 bg-destructive/10 border border-destructive/20 rounded p-3">
                  <p className="text-sm text-destructive font-mono">{execution.error}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle>Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            {execution.metadata ? (
              <pre className="bg-muted p-4 rounded text-xs overflow-x-auto text-foreground max-w-full whitespace-pre-wrap break-all">
                {JSON.stringify(execution.metadata, null, 2)}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhum metadata disponível</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Logs ({logs?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!logs || logs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Nenhum log disponível</div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-3 hover:bg-accent transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Badge className={getLogLevelColor(log.level)}>{log.level}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(log.timestamp, 'HH:mm:ss.SSS')}
                        </span>
                      </div>
                      <p className="text-sm text-foreground font-mono break-words">{log.message}</p>
                      {log.metadata && (
                        <details className="mt-2">
                          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                            Ver metadados
                          </summary>
                          <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto text-foreground max-w-full whitespace-pre-wrap break-all">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Confirmation Modal */}
      <ConfirmDialog
        open={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
        title="Cancelar Execução"
        description={`Tem certeza que deseja cancelar esta execução? A execução será interrompida imediatamente e marcada como cancelada. Esta ação não pode ser desfeita.`}
        confirmText="Cancelar Execução"
        variant="destructive"
        icon="delete"
        onConfirm={handleConfirmCancel}
        isLoading={cancelMutation.isPending}
      />

      {/* Retry Confirmation Modal */}
      <ConfirmDialog
        open={confirmRetryOpen}
        onOpenChange={setConfirmRetryOpen}
        title="Reexecutar Automação"
        description={`Deseja reexecutar esta automação usando os mesmos dados da execução original? Uma nova execução será criada e enfileirada para processamento. Os dados do webhook/trigger original serão preservados.`}
        confirmText="Reexecutar"
        variant="default"
        icon="execute"
        onConfirm={handleConfirmRetry}
        isLoading={retryMutation.isPending}
      />
    </div>
  );
}
